import { createSlice } from '@reduxjs/toolkit';
import collectionsState from '../../../constants/collections-v2/collectionsState';
import { getDefaultConnectorDraft } from '../../../constants/collections-v2/connectors';
import { getDefaultAddContextLabels } from '../../../constants/collections-v2/contextTypes';

// Backend detaches a deleted parent's children into standalone items rather than
// deleting them too, so promote them up to the removed node's level instead of
// discarding the whole subtree. A refetch right after deletion reconciles any stale fields.
const removeItemPromotingChildren = (nodes, key) => {
  const result = [];
  (nodes || []).forEach(item => {
    if (item.key === key) {
      if (item.children?.length) result.push(...item.children);
      return;
    }
    if (item.children?.length) {
      const children = removeItemPromotingChildren(item.children, key);
      result.push({ ...item, children: children.length ? children : undefined });
    } else {
      result.push(item);
    }
  });
  return result;
};

export const collectionsSlice = createSlice({
  name: 'collectionsV2',
  initialState: collectionsState,
  reducers: {
    setCollections: (state, action) => {
      state.data = action.payload;
    },
    setCollectionsEtc: (state, action) => {
      state.etc = action.payload;
    },
    setIsCreateCollectionDrawerOpen: (state, action) => {
      state.etc.isCreateCollectionDrawerOpen = action.payload;
    },
    setCollectionTitle: (state, action) => {
      state.data.title = action.payload;
    },
    setCollectionDescription: (state, action) => {
      state.data.description = action.payload;
    },
    setCollectionStatus: (state, action) => {
      state.etc.status = action.payload;
    },
    setIsAddContextDrawerOpen: (state, action) => {
      state.etc.isAddContextDrawerOpen = action.payload;
    },
    setAddContextStep: (state, action) => {
      state.etc.addContextStep = action.payload;
    },
    setSelectedContextType: (state, action) => {
      state.etc.selectedContextType = action.payload;
    },
    setAddContextLabels: (state, action) => {
      state.etc.addContextLabels = Array.isArray(action.payload) ? action.payload : [];
    },
    setAddContextUseAsReference: (state, action) => {
      state.etc.addContextUseAsReference = Boolean(action.payload);
    },
    setUrlLabelDrafts: (state, action) => {
      state.etc.urlLabelDrafts =
        action.payload && typeof action.payload === 'object' && !Array.isArray(action.payload)
          ? action.payload
          : {};
    },
    setUrlLabelDraft: (state, action) => {
      const { sourceURL, labels } = action.payload || {};
      if (!sourceURL) return;
      state.etc.urlLabelDrafts = {
        ...(state.etc.urlLabelDrafts || {}),
        [sourceURL]: Array.isArray(labels) ? labels : [],
      };
    },
    setUrlUseAsReferenceDrafts: (state, action) => {
      state.etc.urlUseAsReferenceDrafts =
        action.payload && typeof action.payload === 'object' && !Array.isArray(action.payload)
          ? action.payload
          : {};
    },
    setUrlUseAsReferenceDraft: (state, action) => {
      const { sourceURL, useAsReference } = action.payload || {};
      if (!sourceURL) return;
      state.etc.urlUseAsReferenceDrafts = {
        ...(state.etc.urlUseAsReferenceDrafts || {}),
        [sourceURL]: Boolean(useAsReference),
      };
    },
    setStagedMediaFiles: (state, action) => {
      state.etc.stagedMediaFiles = Array.isArray(action.payload) ? action.payload : [];
    },
    appendStagedMediaFile: (state, action) => {
      const file = action.payload;
      if (!file?.mediaId) return;
      const existing = state.etc.stagedMediaFiles || [];
      if (existing.some(item => item.mediaId === file.mediaId)) return;
      state.etc.stagedMediaFiles = [...existing, file];
    },
    removeStagedMediaFile: (state, action) => {
      state.etc.stagedMediaFiles = (state.etc.stagedMediaFiles || []).filter(
        item => item.mediaId !== action.payload
      );
    },
    setStagedMediaFileLabels: (state, action) => {
      const { mediaId, labels } = action.payload || {};
      if (!mediaId) return;
      state.etc.stagedMediaFiles = (state.etc.stagedMediaFiles || []).map(item =>
        item.mediaId === mediaId ? { ...item, labels: Array.isArray(labels) ? labels : [] } : item
      );
    },
    setStagedMediaFileUseAsReference: (state, action) => {
      const { mediaId, useAsReference } = action.payload || {};
      if (!mediaId) return;
      state.etc.stagedMediaFiles = (state.etc.stagedMediaFiles || []).map(item =>
        item.mediaId === mediaId ? { ...item, useAsReference: Boolean(useAsReference) } : item
      );
    },
    setUrlInputText: (state, action) => {
      state.etc.urlInputText = action.payload;
    },
    setPlainTextInput: (state, action) => {
      state.etc.plainTextInput = action.payload;
    },
    setSitemapUrlInput: (state, action) => {
      state.etc.sitemapUrlInput = action.payload;
    },
    setConnectorDraft: (state, action) => {
      state.etc.connectorDraft = {
        ...(state.etc.connectorDraft || getDefaultConnectorDraft()),
        ...action.payload,
      };
    },
    resetConnectorDraft: state => {
      state.etc.connectorDraft = getDefaultConnectorDraft();
    },
    setTypeFilter: (state, action) => {
      state.etc.typeFilter = action.payload;
    },
    setStatusFilter: (state, action) => {
      state.etc.statusFilter = action.payload;
    },
    setSelectedRowKeys: (state, action) => {
      state.etc.selectedRowKeys = action.payload;
    },
    setExpandedRowKeys: (state, action) => {
      state.etc.expandedRowKeys = action.payload;
    },
    setDiscoveringKey: (state, action) => {
      state.etc.discoveringKey = action.payload;
    },
    setSitemapDiscoveredLinks: (state, action) => {
      const { entryPointId, links, sitemapURL, crawlStatus } = action.payload || {};
      if (!entryPointId) return;
      state.etc.sitemapDiscoveredLinks = {
        ...(state.etc.sitemapDiscoveredLinks || {}),
        [entryPointId]: {
          links: Array.isArray(links) ? links : [],
          sitemapURL: sitemapURL || '',
          crawlStatus: crawlStatus || null,
        },
      };
    },
    openFilterUrlsDrawer: (state, action) => {
      const entryPointId = action.payload;
      state.etc.isFilterUrlsDrawerOpen = true;
      state.etc.filterUrlsEntryPointId = entryPointId || null;
      state.etc.filterUrlsSelected = [];
    },
    closeFilterUrlsDrawer: state => {
      state.etc.isFilterUrlsDrawerOpen = false;
      state.etc.filterUrlsEntryPointId = null;
      state.etc.filterUrlsSelected = [];
    },
    setFilterUrlsSelected: (state, action) => {
      state.etc.filterUrlsSelected = Array.isArray(action.payload) ? action.payload : [];
    },
    setCollectionItems: (state, action) => {
      const nextItems = Array.isArray(action.payload) ? action.payload : [];
      state.etc.items = nextItems;

      const nextDiscovered = { ...(state.etc.sitemapDiscoveredLinks || {}) };
      nextItems.forEach(item => {
        if (!item?.isEntryPoint || item.collectionItemType !== 'xmlSitemap') return;
        const entryPointId = item.entryPointId || item.sourceRefId;
        const links = Array.isArray(item.discoveredLinks) ? item.discoveredLinks : [];
        if (!entryPointId || !links.length) return;
        nextDiscovered[entryPointId] = {
          links,
          sitemapURL: item.sourceURL || nextDiscovered[entryPointId]?.sitemapURL || '',
          crawlStatus: item.crawlStatus || nextDiscovered[entryPointId]?.crawlStatus || null,
        };
      });
      state.etc.sitemapDiscoveredLinks = nextDiscovered;
    },
    appendCollectionItems: (state, action) => {
      state.etc.items = [...(state.etc.items || []), ...(action.payload || [])];
    },
    updateCollectionItem: (state, action) => {
      const { key, changes } = action.payload;
      const applyChanges = nodes =>
        (nodes || []).map(item => {
          if (item.key === key) {
            return { ...item, ...changes };
          }
          if (item.children?.length) {
            return { ...item, children: applyChanges(item.children) };
          }
          return item;
        });
      state.etc.items = applyChanges(state.etc.items);
    },
    removeCollectionItem: (state, action) => {
      state.etc.items = removeItemPromotingChildren(state.etc.items, action.payload);
    },
    resetAddContextDrawer: state => {
      state.etc.isAddContextDrawerOpen = false;
      state.etc.addContextStep = 0;
      state.etc.selectedContextType = null;
      state.etc.addContextLabels = getDefaultAddContextLabels();
      state.etc.addContextUseAsReference = true;
      state.etc.urlLabelDrafts = {};
      state.etc.urlUseAsReferenceDrafts = {};
      state.etc.stagedMediaFiles = [];
      state.etc.urlInputText = '';
      state.etc.plainTextInput = '';
      state.etc.sitemapUrlInput = '';
      state.etc.connectorDraft = getDefaultConnectorDraft();
      state.etc.editingContextItem = null;
    },
    openAddContextDrawer: state => {
      state.etc.isAddContextDrawerOpen = true;
      state.etc.addContextStep = 0;
      state.etc.selectedContextType = null;
      state.etc.addContextLabels = getDefaultAddContextLabels();
      state.etc.addContextUseAsReference = true;
      state.etc.urlLabelDrafts = {};
      state.etc.urlUseAsReferenceDrafts = {};
      state.etc.stagedMediaFiles = [];
      state.etc.urlInputText = '';
      state.etc.plainTextInput = '';
      state.etc.sitemapUrlInput = '';
      state.etc.connectorDraft = getDefaultConnectorDraft();
      state.etc.editingContextItem = null;
    },
    openEditPlainTextDrawer: (state, action) => {
      const item = action.payload || {};
      state.etc.isAddContextDrawerOpen = true;
      state.etc.addContextStep = 1;
      state.etc.selectedContextType = 'plainText';
      state.etc.urlInputText = '';
      state.etc.sitemapUrlInput = '';
      state.etc.connectorDraft = getDefaultConnectorDraft();
      state.etc.addContextLabels = getDefaultAddContextLabels();
      state.etc.addContextUseAsReference = true;
      state.etc.urlLabelDrafts = {};
      state.etc.urlUseAsReferenceDrafts = {};
      state.etc.stagedMediaFiles = [];
      state.etc.plainTextInput = item.plainText || '';
      state.etc.editingContextItem = {
        itemId: item.itemId || null,
        key: item.key || null,
        title: item.title || '',
        plainText: item.plainText || '',
        label: item.label || item.labels?.[0] || null,
      };
    },
    setIsSyncSettingsDrawerOpen: (state, action) => {
      state.etc.isSyncSettingsDrawerOpen = action.payload;
    },
    setSyncSettingsDraft: (state, action) => {
      state.etc.syncSettingsDraft = action.payload;
    },
    setSyncSettings: (state, action) => {
      state.data.syncSettings = action.payload;
    },
    setIsAuthSettingsDrawerOpen: (state, action) => {
      state.etc.isAuthSettingsDrawerOpen = action.payload;
    },
    setAuthSettingsDraft: (state, action) => {
      state.etc.authSettingsDraft = action.payload;
    },
    setContentExtractionAuthConfig: (state, action) => {
      state.data.contentExtractionAuthConfig = action.payload;
    },
    setChunksDrawerItem: (state, action) => {
      state.etc.chunksDrawerItem = action.payload;
    },
  },
});

export const {
  setCollections,
  setCollectionsEtc,
  setIsCreateCollectionDrawerOpen,
  setCollectionTitle,
  setCollectionDescription,
  setCollectionStatus,
  setIsAddContextDrawerOpen,
  setAddContextStep,
  setSelectedContextType,
  setAddContextLabels,
  setAddContextUseAsReference,
  setUrlLabelDrafts,
  setUrlLabelDraft,
  setUrlUseAsReferenceDrafts,
  setUrlUseAsReferenceDraft,
  setStagedMediaFiles,
  appendStagedMediaFile,
  removeStagedMediaFile,
  setStagedMediaFileLabels,
  setStagedMediaFileUseAsReference,
  setUrlInputText,
  setPlainTextInput,
  setSitemapUrlInput,
  setConnectorDraft,
  resetConnectorDraft,
  setTypeFilter,
  setStatusFilter,
  setSelectedRowKeys,
  setExpandedRowKeys,
  setDiscoveringKey,
  setSitemapDiscoveredLinks,
  openFilterUrlsDrawer,
  closeFilterUrlsDrawer,
  setFilterUrlsSelected,
  setCollectionItems,
  appendCollectionItems,
  updateCollectionItem,
  removeCollectionItem,
  resetAddContextDrawer,
  openAddContextDrawer,
  openEditPlainTextDrawer,
  setIsSyncSettingsDrawerOpen,
  setSyncSettingsDraft,
  setSyncSettings,
  setIsAuthSettingsDrawerOpen,
  setAuthSettingsDraft,
  setContentExtractionAuthConfig,
  setChunksDrawerItem,
} = collectionsSlice.actions;

export default collectionsSlice.reducer;
